import { datadogRum } from '@datadog/browser-rum';
import BrowserCookies from '~/utilities/BrowserCookies';

const LEGACY_COOKIE = '_dd_s';
const SESSION_EXPIRATION_MS = 15 * 60 * 1000;
const SESSION_TIME_OUT_MS = 4 * 60 * 60 * 1000;

// The replay link carries an `error-type` whenever replay is not active. Its absence is the
// only public signal for "recording right now"; `replay-not-started` is transient at init.
function readReplayState(): 'on' | 'off' | 'unknown' {
  const link = datadogRum.getSessionReplayLink();

  if (!link) return 'unknown';

  if (!link.includes('error-type=')) return 'on';

  return link.includes('error-type=replay-not-started') ? 'unknown' : 'off';
}

function parseFields(value: string): Map<string, string> {
  const fields = new Map<string, string>();

  value
    .split('&')
    .filter(Boolean)
    .forEach(entry => {
      const separator = entry.indexOf('=');

      if (separator > 0) {
        fields.set(entry.slice(0, separator), entry.slice(separator + 1));
      }
    });

  return fields;
}

/**
 * Temporary bridge for `@carvana/core-datadog` below 3.2.2, which reads replay state from the
 * `rum` field of the v6 session cookie. SDK v7 writes `_dd_s_v2` instead and drops that field,
 * so without this those consumers lose the replay link on their events.
 *
 * Only the `rum` field is ever owned here. Identity fields are seeded solely when no v6 app on
 * the domain has written yet, since this cookie is shared and overwriting it would reset their
 * sessions.
 *
 * Remove once every core-datadog consumer resolves 3.2.2 or later.
 *
 * @param assumedReplay used only while the SDK cannot yet report real replay state.
 */
export default function syncLegacyReplayCookie(assumedReplay: boolean) {
  const replayState = readReplayState();
  const hasReplay =
    replayState === 'unknown' ? assumedReplay : replayState === 'on';
  const fields = parseFields(BrowserCookies.get(LEGACY_COOKIE) ?? '');

  if (!fields.has('id')) {
    const sessionId = datadogRum.getInternalContext()?.session_id;

    if (!sessionId) return;

    const now = Date.now();

    fields.set('id', sessionId);
    fields.set('created', String(now));
    fields.set('expire', String(now + SESSION_EXPIRATION_MS));
  }

  fields.set('rum', hasReplay ? '1' : '2');

  // No domain: the SDK only scopes this cookie to the registrable domain when
  // trackSessionAcrossSubdomains is on, which we do not set. Host-only matches it.
  BrowserCookies.set(
    LEGACY_COOKIE,
    Array.from(fields, ([key, value]) => `${key}=${value}`).join('&'),
    { expires: new Date(Date.now() + SESSION_TIME_OUT_MS) }
  );
}
